import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { BarChart, BarChart1, Chart, DonutChart } from 'components/Charts'
import { DatePicker, Select } from 'components/Form'
import Progresbar from './components/Progresbar'
import { getApiData as clientCompanyApiData } from 'services/ClientCompanyService'
import { getApiData as vendorApiData } from 'services/TransportProviderService'
import {
    getCombinedDataApi,
    getCompanyUsageDataApi,
    getInvoiceCount,
    getInvoiceTotal,
    getInvoiceVarrience,
    getPerformer,
    getUserUsageDataApi,
    getVendorReport,
} from 'services/DashboradService'
import { getApiData } from 'services/CompanyService'
import { Modal } from 'components/Ui'
import ViewDetails from './components/ViewDetails'
import TopPerformer from './components/TopPerformer'
import CardDataStats from 'components/CardDataStats'
import { numberFormat } from 'components/helper'
import { HiOutlineCurrencyDollar, HiOutlineDocumentText } from 'react-icons/hi'
import { IoWarningOutline } from 'react-icons/io5'
import { FaBalanceScale } from 'react-icons/fa'
import { setUser } from 'store/auth/userSlice'
import useAuth from 'utils/hooks/useAuth'

injectReducer('dashboard', reducer)

export default function Home() {
    const userInfo = useSelector((state) => state.auth.user)
    const dispatch = useDispatch()
    const { authenticated } = useAuth()

    // State management - grouped by concern
    const [companyData, setCompanyData] = useState([])
    const [clientCompanyData, setClientCompanyData] = useState([])
    const [vendorData, setVendorData] = useState([])

    const [selectedCompany, setSelectedCompany] = useState('')
    const [selectedClient, setSelectedClient] = useState('')
    const [selectedVendor, setSelectedVendor] = useState('')
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)

    const [dashboardData, setDashboardData] = useState({
        topPerformer: [],
        poorPerformer: [],
        userUsage: [],
        companyUsage: null,
        invoiceUpload: null,
        invoiceVariance: null,
        invoiceTotal: null,
        vendorReport: null,
        costPerKg: '',
        serviceCharge: '',
        surcharge: '',
        serviceType: '',
    })

    const [loading, setLoading] = useState({
        initial: true,
        filters: false,
    })
    const [open, setOpen] = useState(false)

    // Initialize dashboard
    useEffect(() => {
        if (authenticated) {
            initializeDashboard()
        }
        // eslint-disable-next-line
    }, [authenticated])

    const initializeDashboard = async () => {
        try {
            setLoading((prev) => ({ ...prev, initial: true }))
            await Promise.all([loadCompanyData(), loadClientCompanyData()])
        } catch (error) {
            console.error('Dashboard initialization error:', error)
        } finally {
            setLoading((prev) => ({ ...prev, initial: false }))
        }
    }

    const loadCompanyData = async () => {
        try {
            const response = await getApiData()
            const companies = response.data.results.map((item) => ({
                ...item,
                label: item.name,
                value: item.id,
            }))

            setCompanyData(companies)

            if (companies.length > 0) {
                const firstCompany = companies[0]
                setSelectedCompany(firstCompany.id)
                handleCurrency(firstCompany)
                await Promise.all([
                    loadVendorData({ company: firstCompany.id }),
                    loadDashboardData({ company: firstCompany.id }),
                ])
            }
        } catch (error) {
            console.error('Error loading company data:', error)
        }
    }

    const loadClientCompanyData = async () => {
        try {
            const response = await clientCompanyApiData()
            const clients = response.data.results
                .filter((item) => item?.client)
                .map((item) => ({
                    ...item,
                    label: item.client.name,
                    value: item.client.id,
                }))
            setClientCompanyData(clients)
        } catch (error) {
            console.error('Error loading client company data:', error)
        }
    }

    const loadVendorData = async (params) => {
        try {
            const response = await vendorApiData(params)
            const vendors =
                response.data?.results
                    ?.filter((item) => item.type === 'Aggregator')
                    ?.map((item) => ({
                        ...item,
                        label: item.name,
                        value: item.id,
                    })) || []
            setVendorData(vendors)
        } catch (error) {
            console.error('Error loading vendor data:', error)
        }
    }

    const loadDashboardData = useCallback(async (params) => {
        try {
            setLoading((prev) => ({ ...prev, filters: true }))

            const [
                combinedData,
                invoiceCount,
                invoiceVariance,
                invoiceTotal,
                vendorReport,
                topPerformer,
                poorPerformer,
                userUsage,
                companyUsage,
            ] = await Promise.all([
                getCombinedDataApi(params),
                getInvoiceCount(params),
                getInvoiceVarrience(params),
                getInvoiceTotal(params),
                getVendorReport(params),
                getPerformer({ ...params, type: 'top' }),
                getPerformer({ ...params, type: 'poor' }),
                getUserUsageDataApi(params),
                getCompanyUsageDataApi(params),
            ])

            // Process vendor report data
            const processedVendorReport = processVendorReportData(
                vendorReport.data,
                params,
            )

            setDashboardData({
                topPerformer: topPerformer.data,
                poorPerformer: poorPerformer.data,
                userUsage: userUsage.data?.results || [],
                companyUsage: companyUsage.data[0],
                invoiceUpload: invoiceCount.data,
                invoiceVariance: invoiceVariance.data,
                invoiceTotal: invoiceTotal.data,
                vendorReport: processedVendorReport,
                costPerKg: combinedData.data.cost_per_kg,
                costPerConnote: combinedData.data.cost_per_connote,
                serviceCharge: combinedData.data.service_charge_summary,
                surcharge: combinedData.data.top_surcharges,
                serviceType: combinedData.data.spend_by_service_type,
            })
        } catch (error) {
            console.error('Error loading dashboard data:', error)
        } finally {
            setLoading((prev) => ({ ...prev, filters: false }))
        }
    }, [])

    const processVendorReportData = (data, params) => {
        if (!data?.month_wise_data) return null

        const months = data.month_wise_data
            .map((item) => Object.keys(item)[0])
            .reverse()

        const series = [
            {
                name: 'Total Cost',
                data: data.month_wise_data
                    .map((item) => {
                        const key = Object.keys(item)[0]
                        return item[key]?.cost || 0
                    })
                    .reverse(),
            },
            {
                name: 'Total Variance',
                data: data.month_wise_data
                    .map((item) => {
                        const key = Object.keys(item)[0]
                        return item[key]?.variance || 0
                    })
                    .reverse(),
            },
        ]

        return {
            total_cost: data.total_cost_six_month,
            total_variance: data.total_variance_six_month,
            series,
            range: params?.start_date ? months : months,
        }
    }

    const handleCurrency = useCallback(
        (company) => {
            if (userInfo?.id) {
                const updatedUser = {
                    ...userInfo,
                    currency_code: 'AUD',
                    company_logo: '',
                }
                dispatch(setUser(updatedUser))
            }
        },
        [userInfo, dispatch],
    )

    const handleCompanyChange = async (companyId) => {
        setSelectedCompany(companyId)
        setSelectedClient('')
        setSelectedVendor('')
        setStartDate(null)
        setEndDate(null)

        const company = companyData.find((c) => c.id === companyId)
        if (company) {
            handleCurrency(company)
        }

        await Promise.all([
            loadVendorData({ company: companyId }),
            loadDashboardData({ company: companyId }),
        ])
    }

    const handleFilterChange = async (filterType, value) => {
        const params = {
            company: selectedCompany,
            client: selectedClient,
            vendor: selectedVendor,
        }

        switch (filterType) {
            case 'client':
                setSelectedClient(value)
                params.client = value
                break
            case 'vendor':
                setSelectedVendor(value)
                params.vendor = value
                break
            default:
                break
        }

        setStartDate(null)
        setEndDate(null)
        await loadDashboardData(params)
    }

    const handleDateChange = async (dateType, value) => {
        if (dateType === 'start') {
            setStartDate(value)
            setEndDate(null)
        } else {
            setEndDate(value)
            if (startDate < value) {
                await loadDashboardData({
                    company: selectedCompany,
                    startdate: startDate,
                    enddate: value,
                })
            }
        }
    }

    // Memoized calculations
    const transactionProgress = useMemo(() => {
        if (!dashboardData.companyUsage) return 0
        const { total_lines_processed, total_lines_remaining } =
            dashboardData.companyUsage
        const total = total_lines_processed + total_lines_remaining
        return total > 0 ? parseInt((total_lines_processed / total) * 100) : 0
    }, [dashboardData.companyUsage])

    const filteredClientCompanyData = useMemo(() => {
        return clientCompanyData.filter((item) => item?.client)
    }, [clientCompanyData])

    if (loading.initial) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading dashboard...</div>
            </div>
        )
    }

    return (
        <div>
            {/* Filters Section */}
            <div className="p-4 mb-6 bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-9">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
                    <Select
                        id="company"
                        name="company"
                        label="Company"
                        placeholder="Select Company"
                        value={selectedCompany}
                        className="min-w-28"
                        options={companyData}
                        onChange={(e) => handleCompanyChange(e.target.value)}
                        disabled={loading.filters}
                    />
                    <Select
                        id="client-company"
                        name="client-company"
                        label="Client Company"
                        placeholder="Select Client Company"
                        value={selectedClient}
                        className="min-w-28"
                        options={filteredClientCompanyData}
                        onChange={(e) =>
                            handleFilterChange('client', e.target.value)
                        }
                        disabled={loading.filters}
                    />
                    <Select
                        id="transport-provider"
                        name="transport-provider"
                        label="Transport Provider"
                        placeholder="Select Transport Provider"
                        value={selectedVendor}
                        className="min-w-28"
                        options={vendorData}
                        onChange={(e) =>
                            handleFilterChange('vendor', e.target.value)
                        }
                        disabled={loading.filters}
                    />
                </div>
            </div>

            {/* Progress and Date Range Section */}
            <div className="p-4 mb-6 bg-white border grid grid-cols-3 rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-9">
                <Progresbar
                    title="Transactions"
                    value={transactionProgress}
                    setIsModalOpen={setOpen}
                />
                <div className="col-span-1 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <DatePicker
                        id="start_date"
                        name="start_date"
                        label="Start Date"
                        placeholder="Start Date"
                        value={startDate}
                        onChange={(e) =>
                            handleDateChange('start', e.target.value)
                        }
                        className="w-full"
                        disabled={loading.filters}
                    />
                    <DatePicker
                        id="end_date"
                        name="end_date"
                        label="End Date"
                        placeholder="End Date"
                        value={endDate}
                        onChange={(e) =>
                            handleDateChange('end', e.target.value)
                        }
                        className="w-full"
                        disabled={loading.filters || !startDate}
                    />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 3xl:grid-cols-4 mb-6">
                <CardDataStats
                    total={
                        dashboardData.invoiceUpload?.current_month_invoices || 0
                    }
                    title="Current Month Invoices"
                    percentage={
                        dashboardData.invoiceUpload?.percentage_increase
                    }
                    description="% change in number of invoices"
                    children={<HiOutlineDocumentText size={40} color="black" />}
                />
                <CardDataStats
                    total={numberFormat(
                        dashboardData.costPerConnote
                            ?.cost_per_connote_verified ?? 0,
                        'AUD',
                    )}
                    title="Average Consignment Cost"
                    children={
                        <HiOutlineCurrencyDollar size={40} color="blue" />
                    }
                />
                <CardDataStats
                    total={numberFormat(
                        dashboardData.invoiceVariance?.total_variance ?? 0,
                        'AUD',
                    )}
                    title="Current Month Average Variance"
                    children={<IoWarningOutline size={40} color="orange" />}
                />
                <CardDataStats
                    total={numberFormat(
                        dashboardData.costPerKg?.cost_per_kg ?? 0,
                        'AUD',
                    )}
                    title="Cost Per Kg"
                    children={<FaBalanceScale size={40} color="red" />}
                />
            </div>

            {/* Charts and Reports */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-6">
                <Chart
                    title="Transport Service Providers Report"
                    data={dashboardData.vendorReport}
                />
                <DonutChart
                    title="Transport Services Report"
                    data={
                        dashboardData.serviceType
                            ? Object.fromEntries(
                                  Object.entries(
                                      dashboardData.serviceType,
                                  ).filter(([key]) =>
                                      ['road', 'air'].includes(
                                          key?.toLowerCase(),
                                      ),
                                  ),
                              )
                            : dashboardData.serviceType
                    }
                />
                <BarChart data={dashboardData.serviceCharge} />
                <BarChart1 data={dashboardData.surcharge} />

                {/* Top Performer */}
                <div className="p-4 mb-6 bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-9">
                    <div className="flex items-center justify-between xl-6">
                        <h4 className="text-black font-bold text-lg">
                            Top Performer
                        </h4>
                    </div>
                    <TopPerformer
                        data={dashboardData.topPerformer}
                        className="lg:col-span-1"
                    />
                </div>

                {/* Underperformer */}
                <div className="p-4 mb-6 bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-9">
                    <div className="flex items-center justify-between mb-6">
                        <h4 className="text-black font-bold text-lg">
                            Underperformer
                        </h4>
                    </div>
                    <TopPerformer
                        data={dashboardData.poorPerformer}
                        className="lg:col-span-1"
                    />
                </div>
            </div>

            {/* Usage Details Modal */}
            <Modal maxWidth="4xl" show={open} onClose={() => setOpen(false)}>
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">
                        Data Usage Details
                    </h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <ViewDetails
                                company={
                                    dashboardData.companyUsage ??
                                    selectedCompany
                                }
                                users={dashboardData.userUsage}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
